import { Formik } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { Box } from '@mui/material';
import { Autorenew, FileCopy } from '@mui/icons-material';
import { TextField, SecretField, SelectField, AlertBoxMsg, Button } from '@qrios/frontend-library';

import CopyToClipboard from 'react-copy-to-clipboard';
import Tooltip from '../../../../../../common/tooltip';
import WithFormLayout from '../../../../../../common/with-form-layout';
import { BusinessError } from '../../../../../../common/business-error';
import { TOGGLE_AUTH_METHOD, TOGGLE_DEBUG_MODE, TOGGLE_STATUS } from '../../../../../../common/status/interfaces';
import { RADIUS } from '../../../../../../../styles/themes';

import locales from '../locales';
import { UssdAppFormData } from '../interfaces';
import { createFormValidations } from './validationSchemas';

interface Props {
  onSubmit: (app: UssdAppFormData) => void;
  onClose: () => void;
  popup: {
    setStateCallback(state: boolean): void;
    setMsgCallback(msg: string): void;
  };
  loading: boolean;
  appIds: string[];
  error?: BusinessError | null;
}

const helpInfoID =
  // eslint-disable-next-line max-len
  'Id must be unique both in the context of your applications and those of other users. We will inform you when the Id is taken.';

const CreateAppForm = ({ onSubmit, onClose, loading, appIds, error = null, popup }: Props) => {
  const handleClipboard = () => {
    popup.setStateCallback(true);
    popup.setMsgCallback(`${locales.form.apiKey.label} copied to clipboard`);
  };
  return (
    <Formik
      initialValues={{
        appId: '',
        appUrl: '',
        status: TOGGLE_STATUS.DISABLED,
        debugEnabled: TOGGLE_DEBUG_MODE.DISABLED,
        authorizationMethod: TOGGLE_AUTH_METHOD.API_KEY,
        apiKey: uuidv4()
      }}
      validationSchema={createFormValidations(appIds)}
      onSubmit={values => {
        const debugEnabledTransformatedToApi = values.debugEnabled === TOGGLE_DEBUG_MODE.ENABLED;
        const ussdAppFormData: UssdAppFormData = {
          appId: values.appId,
          appUrl: values.appUrl,
          status: values.status,
          debugEnabled: debugEnabledTransformatedToApi,
          authorizationMethod: { type: values.authorizationMethod, key: values.apiKey }
        };
        onSubmit(ussdAppFormData);
      }}
    >
      {({ values, handleChange, handleSubmit, errors, touched, handleBlur, setFieldValue }) => (
        <WithFormLayout
          formInfoText="Set values for new USSD app"
          onSubmit={handleSubmit}
          onSecondary={onClose}
          loading={loading}
          error={error}
          secondaryLabel="Close"
          submitLabel="Create USSD app"
        >
          <Box data-testid="dialog-content">
            {values.authorizationMethod === TOGGLE_AUTH_METHOD.API_KEY && (
              <Box mb={2}>
                <AlertBoxMsg severity="info">
                  <p>API key will be sent with all USSD API requests to your application.</p>
                </AlertBoxMsg>
              </Box>
            )}
            <TextField
              handleBlur={handleBlur}
              handleChange={handleChange}
              name="appId"
              label="App Id"
              value={values.appId}
              error={errors.appId}
              touched={touched.appId}
              helpInfo={helpInfoID}
            />
            <Box mb={2}>
              <TextField
                handleBlur={handleBlur}
                handleChange={handleChange}
                name="appUrl"
                label="App URL"
                value={values.appUrl}
                error={errors.appUrl}
                touched={touched.appUrl}
              />
            </Box>
            <Box mb={2}>
              <SelectField
                name="status"
                label={locales.form.status.label}
                value={values.status}
                handleChange={handleChange}
                handleBlur={handleBlur}
                error={errors.status}
                touched={touched.status}
                items={[
                  { label: 'Offline', value: TOGGLE_STATUS.DISABLED },
                  { label: 'Online', value: TOGGLE_STATUS.ENABLED }
                ]}
              />
            </Box>
            <Box mb={2}>
              <SelectField
                name="debugEnabled"
                label={locales.form.debugEnabled.label}
                value={values.debugEnabled}
                handleChange={handleChange}
                handleBlur={handleBlur}
                error={errors.debugEnabled}
                touched={touched.debugEnabled}
                items={[
                  { label: 'Debug mode active', value: TOGGLE_DEBUG_MODE.ENABLED },
                  { label: 'Debug mode disabled', value: TOGGLE_DEBUG_MODE.DISABLED }
                ]}
              />
            </Box>
            <Box mb={2}>
              <SelectField
                name="authorizationMethod"
                label={locales.form.authorizationMethod.label}
                value={values.authorizationMethod}
                handleChange={handleChange}
                handleBlur={handleBlur}
                error={errors.authorizationMethod}
                touched={touched.authorizationMethod}
                items={[
                  { label: 'No authorization', value: TOGGLE_AUTH_METHOD.NO_AUTHORIZATION },
                  { label: 'API key', value: TOGGLE_AUTH_METHOD.API_KEY }
                ]}
              />
            </Box>
            {values.authorizationMethod === TOGGLE_AUTH_METHOD.API_KEY && (
              <Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
                  <SecretField
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    name="apiKey"
                    label={locales.form.apiKey.label}
                    value={values.apiKey}
                    touched={touched.apiKey}
                    disabled={true}
                  />
                  <Tooltip title="Create different API Key" placement="bottom">
                    <Box sx={{ paddingLeft: '16px', paddingTop: '28px' }}>
                      <Button
                        text=""
                        onClick={() => setFieldValue('apiKey', uuidv4())}
                        endIcon={<Autorenew sx={{ fontSize: '25px !important' }} />}
                        style={{ height: '47px', borderRadius: `${RADIUS.sm}px`, paddingLeft: 'revert' }}
                      />
                    </Box>
                  </Tooltip>
                  <Tooltip title="Copy to clipboard">
                    <Box sx={{ paddingLeft: '16px', paddingTop: '28px' }}>
                      <CopyToClipboard text={values.apiKey}>
                        <Button
                          text=""
                          onClick={handleClipboard}
                          endIcon={<FileCopy sx={{ fontSize: '25px !important' }} />}
                          style={{ height: '47px', borderRadius: `${RADIUS.sm}px`, paddingLeft: 'revert' }}
                        />
                      </CopyToClipboard>
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Box>
        </WithFormLayout>
      )}
    </Formik>
  );
};

export default CreateAppForm;
